import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../AuthWrapper';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { proxyFetch } from '../services/Proxy';

function Admin() {
  const { user, roles } = useContext(UserContext);

  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);

  const [licenseProductId, setLicenseProductId] = useState('');
  const [encryptedText, setEncryptedText] = useState('');
  const [decrypted, setDecrypted] = useState({
    userID: '',
    orderNumber: '',
    activationCode: '',
    activationDate: '',
    activationValidity: '',
    licenseType: '',
    internalProductCode: '',
    machineID1: '{}',
    machineID2: '{}',
  });
  const [licenseStatus, setLicenseStatus] = useState('');

  const [codeUploadProductId, setCodeUploadProductId] = useState('');
  const [rawCodes, setRawCodes] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');

  const [codesToRemove, setCodesToRemove] = useState('');
  const [removeStatus, setRemoveStatus] = useState('');

  const CODE_POOL_COLLECTION =
    process.env.REACT_APP_BUILD_TYPE === 'staging'
      ? 'external-code-pool-staging'
      : 'external-code-pool-production';

  const [errorOrders, setErrorOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [reprocessStatus, setReprocessStatus] = useState({});

  const ORDERS_COLLECTION =
    process.env.REACT_APP_BUILD_TYPE === 'staging'
      ? 'orders-staging'
      : 'orders-production';

  useEffect(() => {
    if (user) {
      window.currentUser = user;
      user.getIdToken(true);
      user.getIdTokenResult().then(t => {
        console.log('🧾 Token claims:', t.claims);
      });
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'products'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(list);
    })();
  }, []);

  useEffect(() => {
    if (!licenseProductId) return;
    const prod = products.find(p => p.id === licenseProductId);
    if (prod?.internalProductCode) {
      setDecrypted(prev => ({ ...prev, internalProductCode: prod.internalProductCode }));
    }
  }, [licenseProductId, products]);

  useEffect(() => {
    if (!roles.includes('admin')) return;
    fetchErrorOrders();
  }, [roles]);

  const generateLink = async () => {
    if (!selectedId) return;
    setLoading(true);
    setLink('');
    try {
      const token = await user.getIdToken();
      const data = await proxyFetch(
        'proxy/download-generator/generate-link',
        token,
        'GET',
        '',
        { 'x-product-code': selectedId }
      );
      setLink(data.url);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not generate download link.');
    } finally {
      setLoading(false);
    }
  };

  const decryptLicense = async () => {
    try {
      setLicenseStatus('Decrypting…');
      const token = await user.getIdToken();
      const selectedProduct = products.find(p => p.id === licenseProductId);
      if (!selectedProduct?.publicEncryptionKey) throw new Error('Missing public key');

      const data = await proxyFetch(
        'proxy/encryption-utils/decrypt-license',
        token,
        'POST',
        '',
        {},
        {
          licensePem: encryptedText,
          publicKeyPem: selectedProduct.publicEncryptionKey,
        }
      );
      if (!data.licenseJson) throw new Error('No licenseJson returned');

      const parsed = data.licenseJson;
      setDecrypted({
        userID: parsed.userID || '',
        orderNumber: parsed.orderNumber || '',
        activationCode: parsed.activationCode || '',
        activationDate: parsed.activationDate || '',
        activationValidity: parsed.activationValidity || '',
        licenseType: parsed.licenseType || '',
        internalProductCode: parsed.internalProductCode || '',
        machineID1: JSON.stringify(parsed.machineID1 || {}),
        machineID2: JSON.stringify(parsed.machineID2 || {}),
      });
      setLicenseStatus('✅ License decrypted');
    } catch (err) {
      console.error(err);
      setLicenseStatus('❌ Failed to decrypt license');
    }
  };

  const REQUIRED_FIELDS = [
    'userID', 'orderNumber', 'activationCode', 'activationDate',
    'activationValidity', 'licenseType', 'internalProductCode',
  ];

  const encryptLicense = async () => {
    try {
      for (const f of REQUIRED_FIELDS) {
        if (!decrypted[f]) {
          setLicenseStatus(`❌ Missing required field: ${f}`);
          return;
        }
      }

      setLicenseStatus('Encrypting…');
      const token = await user.getIdToken();
      const selectedProduct = products.find(p => p.id === licenseProductId);
      if (!selectedProduct?.publicEncryptionKey || !selectedProduct?.privateEncryptionKey) {
        throw new Error('Missing key‑pair for selected product.');
      }

      const licenseData = JSON.stringify({
        userID: decrypted.userID,
        orderNumber: decrypted.orderNumber,
        activationCode: decrypted.activationCode,
        activationDate: decrypted.activationDate,
        activationValidity: decrypted.activationValidity,
        licenseType: decrypted.licenseType,
        internalProductCode: decrypted.internalProductCode,
        machineID1: JSON.parse(decrypted.machineID1),
        machineID2: JSON.parse(decrypted.machineID2),
      });

      const data = await proxyFetch(
        'proxy/encryption-utils/encrypt-license',
        token,
        'POST',
        '',
        {},
        {
          licenseJson: licenseData,
          publicKeyPem: selectedProduct.publicEncryptionKey,
          privateKeyPem: selectedProduct.privateEncryptionKey,
        }
      );

      setEncryptedText(data.licensePem);
      setLicenseStatus('✅ License encrypted');
    } catch (err) {
      console.error(err);
      setLicenseStatus('❌ Failed to encrypt license');
    }
  };

  const addCodesToPool = async () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    try {
      const product = products.find(p => p.id === codeUploadProductId);
      if (!product?.productCode) {
        alert('Invalid product selected.');
        return;
      }

      const codes = rawCodes.split('\n').map(c => c.trim()).filter(Boolean);
      if (codes.length === 0) {
        alert('No codes provided.');
        return;
      }

      const confirmed = window.confirm(`Are you sure you want to add ${codes.length} codes to the pool for "${product.name}"?`);
      if (!confirmed) return;

      setUploadStatus(`Uploading ${codes.length} codes...`);
      const token = await user.getIdToken();

      await proxyFetch(
        'proxy/product-utils/add-codes-to-pool',
        token,
        'POST',
        '',
        {},
        {
          collection: CODE_POOL_COLLECTION,
          productCode: product.productCode,
          codes,
        }
      );

      setUploadStatus(`✅ Successfully uploaded ${codes.length} codes.`);
      setRawCodes('');
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadStatus(`❌ Upload failed: ${err.message}`);
    }
  };

  const removeCodesFromPool = async () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    try {
      const product = products.find(p => p.id === codeUploadProductId);
      if (!product?.productCode) {
        alert('Invalid product selected.');
        return;
      }

      const codes = codesToRemove.split('\n').map(c => c.trim()).filter(Boolean);
      if (codes.length === 0) {
        alert('No codes provided for removal.');
        return;
      }

      const confirmed = window.confirm(`Are you sure you want to remove ${codes.length} codes from the pool for "${product.name}"?`);
      if (!confirmed) return;

      setRemoveStatus(`Removing ${codes.length} codes...`);
      const token = await user.getIdToken();

      await proxyFetch(
        'proxy/product-utils/remove-codes-from-pool',
        token,
        'POST',
        '',
        {},
        {
          collection: CODE_POOL_COLLECTION,
          productCode: product.productCode,
          codes,
        }
      );

      setRemoveStatus(`✅ Successfully removed ${codes.length} codes.`);
      setCodesToRemove('');
    } catch (err) {
      console.error('Remove failed:', err);
      setRemoveStatus(`❌ Remove failed: ${err.message}`);
    }
  };

const fetchErrorOrders = async () => {
    setLoadingOrders(true);
    const q = query(
    collection(db, ORDERS_COLLECTION),
    where('orderStatus', 'in', ['error', 'processed'])
    );
    const snap = await getDocs(q);
    setErrorOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoadingOrders(false);
};

const handleReprocess = async orderId => {
  setReprocessStatus(prev => ({ ...prev, [orderId]: 'processing' }));
  try {
    const token = await user.getIdToken();
    await proxyFetch(
      'proxy/webhook-utils/reprocess-order',
      token,
      'POST',
      '',
      {},
      { orderId }
    );
    setReprocessStatus(prev => ({ ...prev, [orderId]: 'reprocessed' }));
  } catch (err) {
    setReprocessStatus(prev => ({ ...prev, [orderId]: err.message }));
  }
};


  const handleLicenceProductChange = e => setLicenseProductId(e.target.value);

  if (!roles || !roles.includes('admin')) return null;

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ color: 'red', fontWeight: 'bold', marginBottom: 16 }}>
        App infrastructure in use:&nbsp;
        <span style={{ textTransform: 'uppercase' }}>{process.env.REACT_APP_BUILD_TYPE}</span>
      </div>

      <h2 style={{ fontSize: 24, marginBottom: 24 }}>Admin Tools</h2>

      {/* Signed URL section */}
      <div style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16, marginBottom: 32 }}>
        <h3>Generate Signed Download Link</h3>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">-- Select Product --</option>
            {products
              .filter(p => p.internalDownload && p.productCode)
              .map(p => (
                <option key={p.productCode} value={p.productCode}>
                  {p.name}
                </option>
              ))}
          </select>
          <button onClick={generateLink} disabled={!selectedId || loading}>
            {loading ? 'Generating…' : 'Generate Link'}
          </button>
        </div>
        {link && (
          <div style={{ marginTop: 16 }}>
            <strong>Download Link:</strong><br />
            <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
          </div>
        )}
      </div>

      {/* Licence playground */}
      <div style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16 }}>
        <h3>License Playground</h3>
        <div style={{ marginBottom: 12 }}>
          <label>
            Product:
            <select
              style={{ marginLeft: 8 }}
              value={licenseProductId}
              onChange={handleLicenceProductChange}
            >
              <option value="">-- Select Product --</option>
              {products
                .filter(p => p.publicEncryptionKey && p.privateEncryptionKey)
                .map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Encrypted License Data:</label>
          <textarea
            value={encryptedText}
            onChange={e => setEncryptedText(e.target.value)}
            rows={4}
            style={{ width: '100%', fontFamily: 'monospace', padding: 8 }}
            placeholder="Paste encrypted license text here"
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={decryptLicense} disabled={!licenseProductId || !encryptedText}>Decrypt</button>
          <button onClick={encryptLicense}>Encrypt</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Decrypted License Data:</label>
          {Object.entries(decrypted).map(([key, value]) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <label>{key}</label>
              <input
                type="text"
                value={value}
                onChange={e => setDecrypted(prev => ({ ...prev, [key]: e.target.value }))}
                style={{ width: '100%', padding: 6 }}
              />
            </div>
          ))}
        </div>

        <p style={{ marginTop: 12, fontStyle: 'italic' }}>{licenseStatus}</p>
      </div>

      {/* External Code Pool Upload & Remove */}
      <div style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16, marginTop: 32 }}>
        <h3>Add Activation Codes to Pool</h3>

        <div style={{ marginBottom: 12 }}>
          <label>
            Product:
            <select
              style={{ marginLeft: 8 }}
              value={codeUploadProductId}
              onChange={e => setCodeUploadProductId(e.target.value)}
            >
              <option value="">-- Select Product --</option>
              {products
                .filter(p => p.activationCodeSource === 'external')
                .map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Activation Codes (one per line):</label>
          <textarea
            rows={6}
            value={rawCodes}
            onChange={e => setRawCodes(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', padding: 8 }}
            placeholder="Enter or paste codes here..."
          />
        </div>

        <button onClick={addCodesToPool} disabled={!codeUploadProductId || !rawCodes.trim()}>
          Upload Codes
        </button>

        {uploadStatus && <p style={{ marginTop: 8, fontStyle: 'italic' }}>{uploadStatus}</p>}

        <hr style={{ margin: '24px 0' }} />

        <h4>Remove Activation Codes from Pool</h4>

        <div style={{ marginBottom: 12 }}>
          <label>Codes to Remove (one per line):</label>
          <textarea
            rows={6}
            value={codesToRemove}
            onChange={e => setCodesToRemove(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', padding: 8 }}
            placeholder="Enter codes to remove..."
          />
        </div>

        <button onClick={removeCodesFromPool} disabled={!codeUploadProductId || !codesToRemove.trim()}>
          Remove Codes
        </button>

        {removeStatus && <p style={{ marginTop: 8, fontStyle: 'italic' }}>{removeStatus}</p>}
      </div>

      {/* Reprocess Error Orders */}
<div style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16, marginTop: 32 }}>
  <h3>Reprocess Error Orders</h3>
{loadingOrders ? (
  <p>Loading error orders…</p>
) : errorOrders.length ? (
  <>
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={async () => {
          if (!window.confirm(`Reprocess all ${errorOrders.length} error orders?`)) return;
          for (const o of errorOrders) {
            await handleReprocess(o.orderId);
          }
        }}
        disabled={Object.values(reprocessStatus).includes('processing')}
      >
        Reprocess All
      </button>
    </div>

    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Order #</th>
          <th style={{ textAlign: 'left' }}>User</th>
          <th style={{ textAlign: 'left' }}>Error</th>
          <th style={{ textAlign: 'left' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {errorOrders.map(o => (
          <tr key={o.id}>
            <td>{o.id}</td>
            <td>{o.userID}</td>
            <td>{o.errorMessage}</td>
            <td>
              <button
                onClick={() => handleReprocess(o.orderId)}
                disabled={reprocessStatus[o.orderId] === 'processing'}
              >
                {reprocessStatus[o.orderId] === 'processing'
                  ? 'Processing…'
                  : 'Reprocess'}
              </button>
              {reprocessStatus[o.orderId] &&
                reprocessStatus[o.orderId] !== 'processing' && (
                  <span style={{ marginLeft: 8 }}>
                    {reprocessStatus[o.orderId]}
                  </span>
                )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
) : (
  <p>No error orders found.</p>
)}

</div>

    </div>
  );
}

export default Admin;
